<template>
  <div class="sectionOuterBox history">
    <div class="sectionInnerBox text-left py-12">
      <h2 class="sr-only">{{ $t('about.timeline.title') }}</h2>
      <v-container class="container timeline-container">
        <div class="year-container" role="tablist" :aria-label="$t('about.timeline.title')">
          <div v-for="item in data" :key="item.year"
            class="year cBlue text24"
            :class="{ 'fontWeight300': activeYear !== item.year, 'fontWeight700': activeYear === item.year }"
            :tabindex="activeYear === item.year ? 0 : -1"
            role="tab"
            :aria-selected="activeYear === item.year"
            :aria-label="$t('about.timeline.yearAriaLabel', { year: item.year })"
            :aria-controls="`card-${item.year}`"
            @click="setActive(item.year)"
            @keydown="handleKeydown($event, item.year)">{{
              item.year }}</div>
        </div>
        <div ref="timelineItems" class="timeline-items" role="tabpanel" :aria-live="'polite'" :aria-label="$t('about.timeline.timelineContentLabel')">
          <div v-for="item in data" :key="item.year" class="timeline-item" :id="`card-${item.year}`"
            :ref="`card-${item.year}`"
            :aria-labelledby="`year-${item.year}`"
            role="tabpanel"
            :aria-hidden="activeYear !== item.year">
            <div class="timeline-circle text-center"><i v-show="activeYear === item.year"
                class="circle-arrow text31 cWhite fal fa-arrow-circle-right" aria-hidden="true"></i></div>
            <v-card :flat="activeYear !== item.year">
              <div class="cWhite text31 fontWeight700 bgBlue text-center mb-4" :id="`year-${item.year}`">{{ item.year }}</div>
              <v-list-item>
                <v-list-item-content>
                  <!-- <div class="text16 fontWeightBold">{{ item.version }}</div> -->
                  <v-list-item-title>
                    <div class="historyListHeader text-wrap text31 fontWeightBold cBlue">{{ $t(`about.timeline.titles.${item.title}`) }}</div>
                  </v-list-item-title>
                  <v-list-item-subtitle>
                    <ul class="sectionList lg pl-0 my-4">
                      <li v-for="feature in item.features" :key="feature"><i
                          class="cBlue fal fa-arrow-circle-right" aria-hidden="true"></i><span class="text-wrap">{{
                            $t(`about.timeline.features.${feature}`) }}</span></li>
                    </ul>
                  </v-list-item-subtitle>
                  <div class="hidden-md hidden-lg hidden-xlg">
                    <v-list-item-avatar tile size="160">
                      <img :src="item.image" alt="">
                    </v-list-item-avatar>
                  </div>
                </v-list-item-content>
                <div class="hidden-xs hidden-sm hidden-xxs">
                  <v-list-item-avatar tile size="160">
                    <img :src="item.image" alt="">
                  </v-list-item-avatar>
                </div>
              </v-list-item>
            </v-card>
          </div>
        </div>
      </v-container>
    </div>
  </div>
</template>

<script>
import { Component, Vue } from 'vue-property-decorator';

@Component
export default class Timeline extends Vue {
  activeYear = 2010;
  data = [
    {
      year: 2010,
      title: '2010',
      image: 'https://cdn.planbook.com/images/welcome-about-rocket.png',
      features: [
        'scheduleByWeekTwoWeekOrCycle',
        'divideLessonsIntoSections',
        'addSchoolEventsAndNoSchoolDays',
        'bumpAndExtendLessons',
        'copyLessonsBetweenClasses'
      ]
    },
    {
      year: 2011,
      title: '2011',
      image: 'https://cdn.planbook.com/images/welcome-about-colors.png',
      features: [
        'customPlanViews',
        'customLessonSections',
        'customDaySchedules',
        'lessonActionOptions',
        'nationalStandards'
      ]
    },
    {
      year: 2012,
      title: '2012',
      image: 'https://cdn.planbook.com/images/welcome-about-paperclip.png',
      features: [
        'monthViewPlans',
        'attachFilesLessons',
        'standardsReporting',
        'administratorAccounts',
        'standardsAllStates'
      ]
    },
    {
      year: 2013,
      title: '2013',
      image: 'https://cdn.planbook.com/images/welcome-about-copy.png',
      features: [
        'copyLessonsTeachers',
        'equalSizeLessonView',
        'removeClassSingleDay',
        'teacherAdministratorComments',
        'classTemplates'
      ]
    },
    {
      year: 2014,
      title: '2014',
      image: 'https://cdn.planbook.com/images/welcome-about-mobile.png',
      features: [
        'lockLessonDate',
        'addExtraClassLessons',
        'createMultipleSchedules',
        'searchCapabilityLessons',
        'iPadiPhoneMobileApps'
      ]
    },
    {
      year: 2015,
      title: '2015',
      image: 'https://cdn.planbook.com/images/welcome-about-share.png',
      features: [
        'dynamicallyShareLessons',
        'moveLessonsDragDrop',
        'groupClassLessonsUnits',
        'assignmentsAssessments',
        'integrationGoogleCalendarDrive',
        'androidMobileApp'
      ]
    },
    {
      year: 2016,
      title: '2016',
      image: 'https://cdn.planbook.com/images/welcome-about-profile.png',
      features: [
        'gradebooks',
        'studentAccounts',
        'todoList',
        'instructionalStrategies',
        'customEventsSeries'
      ]
    },
    {
      year: 2017,
      title: '2017',
      image: 'https://cdn.planbook.com/images/welcome-about-flip.png',
      features: [
        'lessonStatus',
        'horizontalPlanView',
        'lessonBanksLongRange',
        'teacherNotesDayClassStudent',
        'alternateDaySchedules',
        'foreignLanguageSupport',
        'quickLessonEditPriorNext'
      ]
    },
    {
      year: 2018,
      title: '2018',
      image: 'https://cdn.planbook.com/images/welcome-about-ui.png',
      features: [
        'saveMicrosoftWord',
        'saveGoogleDocs',
        'addCustomFonts',
        'uiImprovementsWCAG',
        'integrationGoogleClassroom',
        'addTablesLessons'
      ]
    },
    {
      year: 2019,
      title: '2019',
      image: 'https://cdn.planbook.com/images/welcome-about-desk.png',
      features: [
        'dragDropClassScheduling',
        'joinCodesTeachersStudents',
        'customViewsSubstitutes',
        'enhancedTemplateFunctionality',
        'classSeatingCharts',
        'studentAttendanceTracking',
        'addExtraClassDays'
      ]
    },
    {
      year: 2020,
      title: '2020',
      image: 'https://cdn.planbook.com/images/welcome-about-binders.png',
      features: [
        'districtAdministration',
        'lessonLinking',
        'teacherPermissions',
        'unitEnhancements',
        'schoolLessonBanks',
        'schoolTemplates'
      ]
    },
    {
      year: 2021,
      title: '2021',
      image: 'https://cdn.planbook.com/images/welcome-about-mobile.png',
      features: [
        'teachersCreateClassesUnitsLessons',
        'administratorsReviewPlansManageCalendar',
        'studentsAccessLessonsAssignmentsGrades'
      ]
    },
    {
      year: 2022,
      title: '2022',
      image: 'https://cdn.planbook.com/images/welcome-about-share.png',
      features: [
        'fullSupportOf1EdTechOneRosterOpenStandard',
        'integrationWithClassLinkRosterServer',
        'rosterSchoolsAdministrationAndTeachingStaff',
        'automaticallyAddClassesStudentsAndAcademicSessions',
        'syncWithDistrictSisOnWeeklyOrNightlyBasis'
      ]
    },
    {
      year: 2023,
      title: '2023',
      image: 'https://cdn.planbook.com/images/welcome-about-ui.png',
      features: [
        'changeHistoryPlusUndoRecentItems',
        'lessonLayoutsByIndividualClass',
        'digitalStickersForHolidaysRemindersBirthdays',
        'customizedDisplaysAndNewRemindersPanel',
        'bumpAFullDayOfLessons'
      ]
    },
    {
      year: 2024,
      title: '2024',
      image: 'https://cdn.planbook.com/images/welcome-about-mobile.png',
      features: [
        'singleSignOnWithGoogleMicrosoftClassLinkCleverOrApple',
        'downloadAndPrintClassSchedules',
        'mobileAppAddsBiometricLoginInAppMessagingAndResourcesPage',
        'selectALanguageEnglishFrenchSpanishItalianAndGerman'
      ]
    },
    {
      year: 2025,
      title: '2025',
      image: 'https://cdn.planbook.com/images/welcome-about-binders.png',
      features: [
        'automaticallyAddSchoolYearsForAllTeachers',
        'generateASchoolWideViewToShowAllTeacherPlans',
        'managerAccountsToSimplifySchoolRosteringAndRenewals',
        'newStickerStylesSimpleTraditionalAndModern'
      ]
    }
  ];

  setActive(year) {
    this.activeYear = year;

    // Announce year selection to screen readers
    this.$nextTick(() => {
      const announcement = this.$t('about.timeline.selectedYearAnnouncement', { year });
      this.announceToScreenReader(announcement);

      const container = this.$refs.timelineItems;
      const yearCard = this.$refs[`card-${year}`][0];

      if (container && yearCard) {
        const offsetTop = yearCard.offsetTop;
        container.scrollTo({
          top: offsetTop,
          behavior: 'smooth'
        });
      }
    });
  }

  announceToScreenReader(message) {
    // Create a temporary element for screen reader announcements
    const announcement = document.createElement('div');
    announcement.setAttribute('aria-live', 'polite');
    announcement.setAttribute('aria-atomic', 'true');
    announcement.className = 'sr-only';
    announcement.textContent = message;

    document.body.appendChild(announcement);

    // Remove after announcement is made
    setTimeout(() => {
      document.body.removeChild(announcement);
    }, 1000);
  }

  handleKeydown(event, year) {
    switch (event.key) {
      case 'Enter':
      case ' ':
        event.preventDefault();
        this.setActive(year);
        break;
      case 'ArrowDown':
      case 'ArrowRight':
        event.preventDefault();
        this.navigateToYear(year, 1);
        break;
      case 'ArrowUp':
      case 'ArrowLeft':
        event.preventDefault();
        this.navigateToYear(year, -1);
        break;
      case 'Home':
        event.preventDefault();
        this.navigateToYear(null, -1);
        break;
      case 'End':
        event.preventDefault();
        this.navigateToYear(null, 1);
        break;
    }
  }

  navigateToYear(currentYear, direction) {
    const currentIndex = this.data.findIndex(item => item.year === currentYear);
    let targetIndex;

    if (currentYear === null) {
      // Home or End key pressed
      targetIndex = direction === -1 ? 0 : this.data.length - 1;
    } else {
      targetIndex = currentIndex + direction;
    }

    // Ensure index is within bounds
    if (targetIndex >= 0 && targetIndex < this.data.length) {
      const targetYear = this.data[targetIndex].year;
      this.setActive(targetYear);

      // Focus the new year element
      this.$nextTick(() => {
        const yearElements = document.querySelectorAll('.year');
        if (yearElements[targetIndex]) {
          yearElements[targetIndex].focus();
        }
      });
    }
  }
}
</script>

<style lang="scss" scoped>
.year-container {
  padding-right: 50px;
  width: 80px;
  height: 630px;
  overflow-y: auto;
  overflow-x: hidden;

  display: flex;
  flex-direction: column;
  gap: 12px;

  scrollbar-width: thin;
}

.circle-arrow {
  margin-top: 5px;
}

.timeline-container {
  height: 650px;
  display: flex;
  justify-content: space-between;
}

.year {
  cursor: pointer;
  transition: all 0.2s ease;
  border-radius: 4px;
  padding: 4px 8px;
  margin: 2px 0;
}

.year:hover {
  opacity: 0.7;
  transition: opacity 0.3s ease;
}

.year:focus {
  outline: 2px solid #0057c1;
  outline-offset: 2px;
  background-color: rgba(0, 87, 193, 0.1);
  box-shadow: 0 0 0 3px rgba(0, 87, 193, 0.2);
}

.year:focus:not(:focus-visible) {
  outline: none;
  background-color: transparent;
  box-shadow: none;
}

.timeline-circle {
  position: absolute;
  left: -22px;
  top: 50%;
  width: 40px;
  height: 40px;
  background-color: #0057c1;
  border-radius: 50%;
}

.timeline-items {
  overflow: auto;
  position: relative;
  padding-left: 20px;

  scrollbar-width: thin;
  /* Width of the scrollbar */
  scrollbar-color: rgba(0, 0, 0, 0.5) rgba(0, 0, 0, 0.1);
  /* Thumb color and track color */
}

/* WebKit-based browsers (Chrome, Safari) */
.timeline-items::-webkit-scrollbar {
  width: 12px;
  /* Width of the scrollbar */
}

.timeline-items::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.5);
  /* Color and opacity of the scrollbar thumb */
  border-radius: 6px;
  /* Rounded corners */
}

.scrollable-container::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.1);
  /* Background color of the track */
}

.timeline-item {
  border-left: 4px solid;
  border-left-color: #0057c1;
  padding: 50px 0 50px 50px;
  position: relative;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
</style>
